import { APP_INITIALIZER, DestroyRef, EnvironmentProviders, Inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';
import { BCF_APP_ID, BCF_PRODUCTION_MODE, BCF_RELEASE_VERSION } from '@bcf-v2-configurators/tokens/app/token';
import { BcfAppId, BcfReleaseVersion } from '@bcf-v2-configurators/tokens/app/types';
import { WINDOW } from '@bcf-v2-platforms/platform-apis/window-provider';
import { WorkerSynchronizer, WorkerSynchronizerSpawner } from '@bcf-v2-platforms/platform-worker/worker-synchronizer';
import { getDestroySubjectFromRef } from '@bcf-v2-shared/misc/rx-helpers/get-destroy-subject-from-ref';
import { Cookies } from '@bcf-v2-utilities/cookies-provider/cookies';
import { distinctUntilChanged, fromEvent, map, startWith, Subject, takeUntil } from 'rxjs';
import { gcpManualError$ } from '../common/gcp-manual-error';
import { prepareUnhandledError } from '../common/prepare-unhandled-error';
import { sendToErrorReporting, sendToErrorUnhandledReporting } from '../common/send-to-error-reporting';

export function provideGcpErrorReportingBrowser(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,

      useFactory:
        (
          bcfProductionMode: boolean,
          bcfAppId: BcfAppId,
          bcfReleaseVersion: BcfReleaseVersion,
          window: Window,
          router: Router,
          cookies: Cookies,
          spawner: WorkerSynchronizerSpawner,
          destroyRef: DestroyRef
        ) =>
        () => {
          if (!bcfProductionMode) {
            return;
          }
          const urlChannel: WorkerSynchronizer<string> = spawner.spawn<string>('gcpErrorReportingUrl');
          let currentUrl: string | undefined = undefined;

          const destroy$: Subject<void> = getDestroySubjectFromRef(destroyRef);
          router.events
            .pipe(
              map(() => router.url),
              startWith(router.url),
              distinctUntilChanged(),
              takeUntil(destroy$)
            )
            .subscribe((url: string) => {
              currentUrl = url;
              urlChannel.next(url);
            });

          fromEvent<PromiseRejectionEvent>(window, 'unhandledrejection')
            .pipe(takeUntil(destroy$))
            .subscribe((error: PromiseRejectionEvent) => {
              sendToErrorUnhandledReporting(
                bcfAppId,
                bcfReleaseVersion,
                prepareUnhandledError(error),
                currentUrl,
                cookies.getEagerAll(),
                'ng-production-browser'
              );
            });

          fromEvent<ErrorEvent>(window, 'error')
            .pipe(takeUntil(destroy$))
            .subscribe((error: ErrorEvent) => {
              if (error.error) {
                const errorToSend: Error | undefined = error.error;
                sendToErrorReporting(
                  bcfAppId,
                  bcfReleaseVersion,
                  errorToSend,
                  currentUrl,
                  cookies.getEagerAll(),
                  'ng-production-browser'
                );
              }
            });

          gcpManualError$.pipe(takeUntil(destroy$)).subscribe((error: Error) => {
            sendToErrorReporting(
              bcfAppId,
              bcfReleaseVersion,
              error,
              currentUrl,
              cookies.getEagerAll(),
              'ng-production-browser'
            );
          });
        },
      deps: [
        [new Inject(BCF_PRODUCTION_MODE)],
        [new Inject(BCF_APP_ID)],
        [new Inject(BCF_RELEASE_VERSION)],
        [new Inject(WINDOW)],
        Router,
        Cookies,
        WorkerSynchronizerSpawner,
        DestroyRef
      ],
      multi: true
    }
  ]);
}
